import React, { FC } from "react";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import {
  Modal,
  notification,
  Switch,
  Select,
  Spin,
  Input,
  DatePicker,
  TimePicker,
} from "antd";
import Title from "antd/es/typography/Title";
import { Colors } from "shared/themes";
import { handleRequestError } from "shared/helpers";
import {
  useGetReportsSchedulesByIdQuery,
  usePatchReportSchedulesMutation,
} from "shared/api";
import { ModalSizeValue } from "shared/constants";
import dayjs from "dayjs";
import { Formik, Form } from "formik";
import * as Yup from "yup";

dayjs.extend(utc);
dayjs.extend(timezone);

interface UpdateReportScheduleModalProps {
  isOpen: boolean;
  closeModal: () => void;
  recipientOptions: { value: string }[];
  scheduleId: number | null;
  formattedReportsListValue?:
    | {
        label: string;
        value: number;
      }[]
    | undefined;
}

const validationSchema = Yup.object({
  report: Yup.number().required("Report is required"),
  recipients: Yup.array()
    .of(Yup.string().email("Invalid email"))
    .min(1, "At least one recipient required")
    .required(),
  email_subject: Yup.string().required("Email subject is required"),
});

const UpdateReportScheduleModal: FC<UpdateReportScheduleModalProps> = ({
  isOpen,
  closeModal,
  recipientOptions,
  scheduleId,
  formattedReportsListValue,
}) => {
  const { data: schedule, isFetching } = useGetReportsSchedulesByIdQuery(
    { id: scheduleId! },
    { skip: !scheduleId }
  );

  const [patchReportSchedule] = usePatchReportSchedulesMutation();
  const [api, contextHolder] = notification.useNotification();

  const storedTimeZone =
    typeof window !== "undefined"
      ? localStorage.getItem("timezone") || dayjs.tz.guess()
      : dayjs.tz.guess();

  const initialValues = {
    recipients: [] as string[],
    email_subject: "",
    email_body: "",
    is_recurrent: true,
    periodicity: "daily",
    time: null as string | null,
    start_periodicity: null as string | null,
    end_periodicity: null as string | null,
    enabled: true,
    report: undefined as number | undefined,
  };

  const formattedInitialValues = schedule
    ? {
        recipients: Object.values(schedule.recipients || {}),
        email_subject: schedule.email_subject || "",
        email_body: schedule.email_body || "",
        is_recurrent: schedule.is_recurrent ?? true,
        periodicity: schedule.periodicity || "daily",
        time: schedule.time
          ? dayjs
              .utc(`${dayjs().format("YYYY-MM-DD")}T${schedule.time}`)
              .tz(storedTimeZone)
              .format("HH:mm:ss")
          : null,
        start_periodicity: schedule.start_periodicity || null,
        end_periodicity: schedule.end_periodicity || null,
        enabled: schedule.enabled ?? true,
        report: schedule.report,
      }
    : initialValues;

  const handleSubmit = async (values: typeof initialValues) => {
    const { recipients, time, start_periodicity, end_periodicity } = values;

    const trimmedRecipients = recipients.map((email) => email.trim());
    const uniqueRecipients = new Set(trimmedRecipients);

    if (uniqueRecipients.size !== trimmedRecipients.length) {
      api.error({
        message: "Validation",
        description: "Duplicate emails are not allowed",
      });
      return;
    }

    try {
      await patchReportSchedule({
        id: scheduleId!,
        data: {
          recipients: trimmedRecipients,
          email_body: values.email_body,
          email_subject: values.email_subject,
          is_recurrent: values.is_recurrent,
          periodicity: values.periodicity || null,
          time: time
            ? dayjs
                .tz(`${dayjs().format("YYYY-MM-DD")}T${time}`, storedTimeZone)
                .utc()
                .format("HH:mm")
            : null,
          start_periodicity: start_periodicity
            ? dayjs(start_periodicity).utc().toISOString()
            : null,
          end_periodicity: end_periodicity
            ? dayjs(end_periodicity).utc().toISOString()
            : null,
          enabled: values.enabled,
          report: values.report!,
        },
      }).unwrap();

      api.success({
        message: "Success",
        description: "Report schedule updated successfully",
      });

      closeModal();
    } catch (error) {
      const description = handleRequestError(
        error,
        "Error while updating schedule"
      );
      api.error({ message: "Error", description });
    }
  };

  return (
    <>
      {contextHolder}
      <Modal
        title={
          <div className="flex max-w-[95%] gap-2 items-center justify-between w-full flex-wrap">
            <span>Edit Report Schedule</span>
          </div>
        }
        open={isOpen}
        onCancel={closeModal}
        footer={null}
        width={ModalSizeValue.PRIMARY}
      >
        {isFetching ? (
          <div className="flex justify-center py-10">
            <Spin />
          </div>
        ) : (
          <Formik
            initialValues={formattedInitialValues}
            validationSchema={validationSchema}
            enableReinitialize
            onSubmit={handleSubmit}
          >
            {({ values, setFieldValue, handleSubmit, errors, touched }) => (
              <Form>
                <div className="flex gap-3 flex-wrap justify-end mb-3">
                  <div className="flex items-center gap-2">
                    <span className="text-sm">Recurrent</span>
                    <Switch
                      checked={values.is_recurrent}
                      onChange={(checked) =>
                        setFieldValue("is_recurrent", checked)
                      }
                    />
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-sm">Enabled</span>
                    <Switch
                      checked={values.enabled}
                      onChange={(checked) => setFieldValue("enabled", checked)}
                    />
                  </div>
                </div>

                {/* Report */}
                <Title level={5} className="text-[14px] mt-4">
                  Report
                </Title>
                <Select
                  style={{ width: "100%" }}
                  placeholder="Select report"
                  value={values.report}
                  onChange={(v) => setFieldValue("report", v)}
                  options={formattedReportsListValue || []}
                />
                {touched.report && errors.report && (
                  <p className="text-red-500 text-sm mt-1">{errors.report}</p>
                )}

                {/* Recipients */}
                <Title level={5} className="text-[14px] mt-4">
                  Recipients
                </Title>
                <Select
                  mode="tags"
                  style={{ width: "100%" }}
                  placeholder="Add or select recipients"
                  value={values.recipients}
                  onChange={(vals) => setFieldValue("recipients", vals)}
                  options={recipientOptions.map((item) => ({
                    label: item.value,
                    value: item.value,
                  }))}
                />
                {touched.recipients && errors.recipients && (
                  <p className="text-red-500 text-sm mt-1">{`${errors.recipients}`}</p>
                )}

                {/* Periodicity */}
                <Title level={5} className="text-[14px] mt-4">
                  Periodicity
                </Title>
                <Select
                  value={values.periodicity}
                  onChange={(v) => setFieldValue("periodicity", v)}
                  style={{ width: "100%" }}
                >
                  <Select.Option value="daily">Daily</Select.Option>
                  <Select.Option value="weekly">Weekly</Select.Option>
                  <Select.Option value="monthly">Monthly</Select.Option>
                  <Select.Option value="yearly">Yearly</Select.Option>
                </Select>

                {/* Time */}
                <Title level={5} className="text-[14px] mt-4">
                  Time
                </Title>
                <TimePicker
                  allowClear
                  style={{ width: "100%" }}
                  format="HH:mm"
                  value={
                    values.time ? dayjs(`1970-01-01T${values.time}`) : null
                  }
                  onChange={(val) =>
                    setFieldValue(
                      "time",
                      val ? dayjs(val).format("HH:mm:ss") : null
                    )
                  }
                />

                {/* Start + End Periodicity */}
                <div className="flex gap-4 mt-4">
                  <div className="w-1/2">
                    <Title level={5} className="text-[14px]">
                      Start Periodicity
                    </Title>
                    <DatePicker
                      allowClear
                      style={{ width: "100%" }}
                      value={
                        values.start_periodicity
                          ? dayjs(values.start_periodicity)
                          : null
                      }
                      onChange={(date) =>
                        setFieldValue(
                          "start_periodicity",
                          date ? date.toISOString() : null
                        )
                      }
                    />
                  </div>
                  <div className="w-1/2">
                    <Title level={5} className="text-[14px]">
                      End Periodicity
                    </Title>
                    <DatePicker
                      allowClear
                      style={{ width: "100%" }}
                      value={
                        values.end_periodicity
                          ? dayjs(values.end_periodicity)
                          : null
                      }
                      onChange={(date) =>
                        setFieldValue(
                          "end_periodicity",
                          date ? date.toISOString() : null
                        )
                      }
                    />
                  </div>
                </div>

                {/* Email Subject */}
                <Title level={5} className="text-[14px] mt-4">
                  Email Subject
                </Title>
                <Input
                  placeholder="Enter email subject"
                  value={values.email_subject}
                  onChange={(e) =>
                    setFieldValue("email_subject", e.target.value)
                  }
                />
                {touched.email_subject && errors.email_subject && (
                  <p className="text-red-500 text-sm mt-1">
                    {errors.email_subject}
                  </p>
                )}

                {/* Email Body */}
                <Title level={5} className="text-[14px] mt-4">
                  Email Body
                </Title>
                <Input.TextArea
                  placeholder="Enter email body"
                  value={values.email_body}
                  onChange={(e) => setFieldValue("email_body", e.target.value)}
                  rows={3}
                />

                <div className="flex justify-end mt-6">
                  <button
                    type="button"
                    onClick={closeModal}
                    className="mr-3 text-gray-500"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    onClick={() => handleSubmit()}
                    className="px-4 py-2 rounded text-white"
                    style={{ background: Colors.blue.primary }}
                  >
                    Save
                  </button>
                </div>
              </Form>
            )}
          </Formik>
        )}
      </Modal>
    </>
  );
};

export { UpdateReportScheduleModal };
